import React from 'react';
import { motion } from 'framer-motion';
import { Badge as BadgeType } from '../types';

// Sample badge data for demonstration
const sampleBadge: BadgeType = {
  type: 'perfect',
  code: 'GCP12345',
  issueDate: new Date().toISOString(),
  validUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
};

interface BadgeDesignProps {
  badge: BadgeType;
  className?: string;
}

// Design 1: Premium Gold Badge (Large, Elegant)
export const PremiumBadge: React.FC<BadgeDesignProps> = ({ badge, className = '' }) => {
  return (
    <div className={`${className}`}>
      <motion.div
        className="relative"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Main Badge */}
        <div className="relative overflow-hidden">
          <div className="bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-600 p-6 rounded-lg shadow-lg border-2 border-yellow-200">
            <div className="absolute top-0 left-0 w-full h-full bg-white opacity-10 rounded-lg"></div>
            
            {/* Badge Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="ml-2 text-2xl font-bold text-white tracking-wider">GoodCart</span>
              </div>
              <div className="bg-white bg-opacity-30 px-3 py-1 rounded-full">
                <span className="text-white font-semibold tracking-wide">PREMIUM</span>
              </div>
            </div>
            
            {/* Badge Content */}
            <div className="text-center py-4">
              <h2 className="text-3xl font-bold text-white mb-2 tracking-wide">PERFECT 100%</h2>
              <div className="w-16 h-1 bg-white mx-auto mb-4 rounded-full"></div>
              <p className="text-white text-opacity-90 mb-4">
                This product has achieved excellence in all quality criteria
              </p>
              
              {/* Badge Score */}
              <div className="bg-white bg-opacity-20 rounded-lg p-3 inline-block">
                <div className="text-5xl font-bold text-white">100</div>
                <div className="text-white text-opacity-80 text-sm">QUALITY SCORE</div>
              </div>
            </div>
            
            {/* Badge Footer */}
            <div className="mt-4 pt-4 border-t border-white border-opacity-20 flex justify-between items-center">
              <div className="text-white text-opacity-90 text-sm">
                <div>Code: {badge.code}</div>
                <div>Issued: {new Date(badge.issueDate).toLocaleDateString()}</div>
              </div>
              <div className="bg-white bg-opacity-20 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        {/* Shine Effect */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-lg pointer-events-none">
          <motion.div 
            className="absolute top-0 -left-3/4 w-1/2 h-full bg-gradient-to-r from-transparent via-white to-transparent skew-x-12 transform opacity-30"
            animate={{ 
              left: ['0%', '150%'],
              opacity: [0, 0.3, 0]
            }}
            transition={{ 
              repeat: Infinity,
              repeatDelay: 5,
              duration: 1.5,
              ease: "easeInOut"
            }}
          />
        </div>
      </motion.div>
      
      {/* QR Code Section */}
      <div className="mt-4 bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-gray-800 font-semibold">Verify Authenticity</h3>
            <p className="text-gray-600 text-sm">Scan to validate this badge</p>
          </div>
          <div className="bg-gray-200 p-2 rounded-md">
            {/* Placeholder for QR code */}
            <div className="w-16 h-16 grid grid-cols-4 grid-rows-4 gap-0.5">
              {Array(16).fill(0).map((_, i) => (
                <div key={i} className={`${Math.random() > 0.3 ? 'bg-gray-800' : 'bg-gray-200'}`}></div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <p>Valid until: {new Date(badge.validUntil || '').toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

// Design 2: Minimalist Modern Badge (Medium, Clean)
export const MinimalistBadge: React.FC<BadgeDesignProps> = ({ badge, className = '' }) => {
  return (
    <div className={`${className}`}>
      <motion.div
        className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
        whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
        transition={{ duration: 0.2 }}
      >
        {/* Top Accent Bar */}
        <div className="h-2 bg-gradient-to-r from-brand-green-500 to-brand-blue-500"></div>
        
        <div className="p-5">
          {/* Badge Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-green-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="ml-1.5 text-sm font-medium text-gray-800">GoodCart</span>
            </div>
            <span className="text-xs font-medium text-gray-500">{badge.code}</span>
          </div>
          
          {/* Badge Content */}
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Perfect 100%</h2>
            <div className="mt-1 text-sm text-gray-600">Highest Quality Standard</div>
          </div>
          
          {/* Badge Score */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              <svg className="w-20 h-20" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#E5E7EB"
                  strokeWidth="3"
                  strokeDasharray="100, 100"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="3"
                  strokeDasharray="100, 100"
                />
                <text x="18" y="21" textAnchor="middle" fontSize="10" fill="#111827" fontWeight="bold">100%</text>
              </svg>
            </div>
          </div>
          
          {/* Badge Info */}
          <div className="flex justify-between items-center text-xs text-gray-500 border-t border-gray-100 pt-3">
            <div>Issued: {new Date(badge.issueDate).toLocaleDateString()}</div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
              <span>Active</span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Design 3: Detailed Information Badge (Comprehensive)
export const DetailedBadge: React.FC<BadgeDesignProps> = ({ badge, className = '' }) => {
  return (
    <div className={`${className}`}>
      <motion.div
        className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      >
        {/* Badge Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-white p-1.5 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="ml-3">
              <h2 className="text-xl font-bold text-white">GoodCart Perfect</h2>
              <p className="text-indigo-100 text-sm">100% Quality Score</p>
            </div>
          </div>
          <div className="bg-indigo-800 bg-opacity-30 px-3 py-1 rounded-lg">
            <span className="text-white text-sm font-medium">{badge.code}</span>
          </div>
        </div>
        
        {/* Badge Content */}
        <div className="p-6">
          {/* Criteria Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Quality Criteria</h3>
            <div className="space-y-3">
              {[
                { name: 'Ingredient Quality', score: 100 },
                { name: 'Sustainability', score: 100 },
                { name: 'Ethical Sourcing', score: 100 },
                { name: 'Health Benefits', score: 100 }
              ].map((criterion, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-1/2">
                    <span className="text-sm text-gray-700">{criterion.name}</span>
                  </div>
                  <div className="w-1/2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-600 rounded-full" 
                        style={{ width: `${criterion.score}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-end mt-1">
                      <span className="text-xs text-gray-500">{criterion.score}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Verification Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Verification Details</h3>
              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">Verified</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Issue Date</p>
                <p className="font-medium text-gray-900">{new Date(badge.issueDate).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Valid Until</p>
                <p className="font-medium text-gray-900">{badge.validUntil ? new Date(badge.validUntil).toLocaleDateString() : 'No Expiration'}</p>
              </div>
              <div>
                <p className="text-gray-500">Issuer</p>
                <p className="font-medium text-gray-900">GoodCart Certification</p>
              </div>
              <div>
                <p className="text-gray-500">Verification Method</p>
                <p className="font-medium text-gray-900">Third-party Lab Testing</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Badge Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <span>Scan QR code to verify authenticity</span>
          </div>
          <div className="bg-white p-2 rounded-md shadow-sm">
            {/* Placeholder for QR code */}
            <div className="w-10 h-10 grid grid-cols-4 grid-rows-4 gap-0.5">
              {Array(16).fill(0).map((_, i) => (
                <div key={i} className={`${Math.random() > 0.3 ? 'bg-gray-800' : 'bg-white'}`}></div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Component to showcase all badge designs
const BadgeDesigns: React.FC = () => {
  return (
    <div className="p-8 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">GoodCart Badge Designs</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Premium Badge */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Premium Badge</h2>
            <p className="text-gray-600 mb-4">Elegant, gold-themed badge with premium visual elements and verification QR code.</p>
            <PremiumBadge badge={sampleBadge} />
          </div>
          
          {/* Minimalist Badge */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Minimalist Badge</h2>
            <p className="text-gray-600 mb-4">Clean, modern design with essential information and a circular progress indicator.</p>
            <MinimalistBadge badge={sampleBadge} />
          </div>
          
          {/* Detailed Badge */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Detailed Badge</h2>
            <p className="text-gray-600 mb-4">Comprehensive badge with detailed quality criteria, verification information, and QR code.</p>
            <DetailedBadge badge={sampleBadge} />
          </div>
        </div>
        
        <div className="mt-12 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Badge Usage Guidelines</h2>
          <p className="text-gray-600 mb-4">
            These badges are designed to be displayed on product pages, marketing materials, and physical packaging. 
            Each design serves a different purpose and can be used in different contexts:
          </p>
          
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li><strong>Premium Badge:</strong> Ideal for high-end products and featured prominently on product detail pages.</li>
            <li><strong>Minimalist Badge:</strong> Perfect for product cards, search results, and mobile interfaces where space is limited.</li>
            <li><strong>Detailed Badge:</strong> Best for dedicated verification pages and detailed product information sections.</li>
          </ul>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800">Integration Note</h4>
                <p className="text-xs text-blue-700 mt-1">
                  All badges include interactive elements using Framer Motion for hover and tap animations. 
                  The premium badge also includes a subtle shine effect for added visual appeal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeDesigns;